import React, { useEffect, useMemo, useState } from 'react'
import {
  IonButton,
  IonButtons,
  IonCard,
  IonCardContent,
  IonCol,
  IonGrid,
  IonIcon,
  IonItem,
  IonItemDivider,
  IonItemOption,
  IonItemOptions,
  IonItemSliding,
  IonLabel,
  IonList,
  IonRow,
  IonSpinner,
  IonTextarea,
  IonToggle,
  useIonViewWillEnter,
} from '@ionic/react'
import { addCircleOutline, search, trash, trashOutline } from 'ionicons/icons'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import SingleSelect from '../../../components/singleSelect/SingleSelect.component'
import { getAddMedicationAdministeredSelector } from '../selectors/addMedicationAdministered.selector'
import { IAddMedicationAdministered } from '../store/addMedicationAdministered.types'
import NumberPad from '../../../components/numberPad/NumberPad.component'
import { useHistory } from 'react-router'
import { TabConstants } from '../../../components/tabs/Tabs.constants'
import {
  addMedicationAdministered,
  deleteMedicationAdministered,
  saveMedicationAdministered,
  setAuthorization,
  setComments,
  setComplications,
  setConcentration,
  setDate,
  setDose,
  setDoseUnit,
  setLotNumber,
  setMedicationAdministered,
  setMedication,
  setAssessedBy,
  setRate,
  setRateUnit,
  setResponseValue,
  setRoute,
  setTime,
  setWasted,
  setWitness1,
  setWitness2,
  setRolePerforming,
  setCurrentMedication,
  setPTA,
  setMedicationDefaults,
  setRolePerformingPNNV,
  setDoseUnitPNNV,
  setRoutePNNV,
  setResponseValuePNNV,
  setAdministeredByAnotherUnit,
  setAdministeredByAnotherUnitPNNV,
} from '../store/addMedicationAdministered.actions'
import TimesRow from '../../times/timesRow/TimesRow.component'
import { AddMedicationAdministeredConstants } from '../store/addMedicationAdministered.constants'
import MultiSelect from '../../../components/multiSelect/MultiSelect.component'
import { numberPadKeysWithDecimal } from '../../../components/numberPad/NumberPad.types'
import './AddMedicationAdministered.content.css'
import textAreaStyles from '../../../components/textArea/TextArea.component.module.css'
import { getAddMedicationAdministeredOptionsSelector } from '../../options/selectors'
import Validation from '../../../components/validation/Validation.component'
import useDeviceMetrics from '../../../utilities/useDeviceMetrics'
import { IOption, IPNNVOption } from '../../../common/common.types'
import { getStyleForPHI, IPHIComponent } from '../../../components/phi/phi.component'
import SInput from '../../../components/stringInput/StringInput.component'
import { getPatientTimelineSelector } from '../../timeline/selectors/patientTimeline.selector'
import { getProcedureStateSelector } from '../../../components/procedure/selectors'
import { getTimesSelector } from '../../times/selectors/getTimes.selector'
import { isValidZollNowDate, isValidZollTime } from '../../../utilities/dateTime.util'
import {
  getAgeRangeGroupId,
  getDefaultsForAgeRange,
  getPTADateTimeErrorMessage,
  isProcDateTimePTA,
} from '../../../utilities/procedures.util'
import { showAlert } from '../../../components/alert/store/alert.actions'
import { IServiceConfig } from '../../configurations/store/configurations.types'
import { getServiceConfigSelector } from '../../configurations/selectors/serviceConfig.selector'
import { syncPtaWithParentVitals } from '../../timeline/store/timeline.actions'
import { getDefaultsSelector } from '../../defaults/selectors/defaults.selector'
import { getPatientDemographicsSelector } from '../../patientDemographics/selectors'
import { initialAddMedicationAdministeredState } from '../store/addMedicationAdministered.reducers'
import { compareObjectsIfEqual, filterOutObjectKeys } from '../../../utilities/object.util'

const AddMedicationAdministeredContent: React.FC<IPHIComponent> = (props: IPHIComponent) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { isPHI } = props
  const history = useHistory()
  const addMedicationAdministeredState = useSelector(getAddMedicationAdministeredSelector)

  const {
    date,
    time,
    pta,
    assessedBy,
    addMedicationAdministeredData,
    authorization,
    comments,
    rolePerforming,
    rolePerformingPNNV,
    administeredByAnotherUnit,
    administeredByAnotherUnitPNNV,
    currentMedication,
    isEkgImport,
    medicationDefaults,
    key,
  } = addMedicationAdministeredState

  const {
    performedByOptions,
    doseUnitOptions,
    routeOptions,
    rateUnitOptions,
    responseOptions,
    complicationsOptions,
    authorizationOptions,
    administeredByAnotherUnitOptions,
  } = useSelector(getAddMedicationAdministeredOptionsSelector)
  const { defaults } = useSelector(getDefaultsSelector)
  const { dOB, age, ageUnit } = useSelector(getPatientDemographicsSelector)

  const { isTablet: isTabletDevice } = useDeviceMetrics()
  const [isFocused, setIsFocused] = useState(false)
  const timelineState = useSelector(getPatientTimelineSelector)
  const { procedureLoader } = timelineState

  const procedure = useSelector(getProcedureStateSelector)
  const { rolePerformingOptions, isFirstVisit } = procedure
  const { atPatientDate, atPatientTime, onSceneDate, onSceneTime } = useSelector(getTimesSelector)

  const { DISPLAY_SECONDS }: IServiceConfig = useSelector(getServiceConfigSelector)
  const displaySeconds = !!DISPLAY_SECONDS

  const onFocus = () => {
    setIsFocused(true)
  }

  const onBlur = () => {
    setIsFocused(false)
  }

  const runPTAValidation = () => {
    const errMsg = getPTADateTimeErrorMessage(
      atPatientDate,
      atPatientTime,
      onSceneDate,
      onSceneTime,
      date,
      time,
      displaySeconds
    )
    if (errMsg) {
      dispatch(showAlert({ message: errMsg }))
      dispatch(setPTA(false))
      dispatch(syncPtaWithParentVitals(false))
    }
  }

  const hasMedicationValue = (currentMedication: IAddMedicationAdministered) => {
    const fieldsToFilter = ['complications', 'key']
    const currentState = filterOutObjectKeys(currentMedication, fieldsToFilter)
    const initalState = {
      dose: initialAddMedicationAdministeredState.dose,
      doseUnit: initialAddMedicationAdministeredState.doseUnit,
      doseUnitPNNV: initialAddMedicationAdministeredState.doseUnitPNNV,
      medication: initialAddMedicationAdministeredState.medication,
      rate: initialAddMedicationAdministeredState.rate,
      rateUnit: initialAddMedicationAdministeredState.rateUnit,
      frequency: initialAddMedicationAdministeredState.frequency,
      route: initialAddMedicationAdministeredState.route,
      routePNNV: initialAddMedicationAdministeredState.routePNNV,
      concentration: initialAddMedicationAdministeredState.concentration,
      response: initialAddMedicationAdministeredState.response,
      responsePNNV: initialAddMedicationAdministeredState.responsePNNV,
      wasted: initialAddMedicationAdministeredState.wasted,
      lotNumber: initialAddMedicationAdministeredState.lotNumber,
      witness1: initialAddMedicationAdministeredState.witness1,
      witness2: initialAddMedicationAdministeredState.witness2,
    }
    return !compareObjectsIfEqual(currentState, initalState)
  }

  useEffect(() => {
    if (pta && isValidZollNowDate(date)) {
      runPTAValidation()
    } else if (!pta && isValidZollNowDate(date) && isValidZollTime(time)) {
      if (isProcDateTimePTA(atPatientDate, atPatientTime, onSceneDate, onSceneTime, date, time, displaySeconds)) {
        dispatch(setPTA(true))
        dispatch(syncPtaWithParentVitals(true))
      }
    }
  }, [date, time, pta])

  const setAssessedByOnChange = (option: IOption) => {
    dispatch(setAssessedBy(option))
    save()
  }

  const setRolePerformingOnChange = (option: IOption) => {
    dispatch(setRolePerforming(option))
    save()
  }

  const setRolePerformingPNNVOnChange = (option: IPNNVOption | null) => {
    dispatch(setRolePerformingPNNV(option))
    save()
  }

  const setAdministeredByAnotherUnitOnChange = (option: IOption) => {
    dispatch(setAdministeredByAnotherUnit(option))
    save()
  }

  const setAdministeredByAnotherUnitPNNVOnChange = (option: IPNNVOption | null) => {
    dispatch(setAdministeredByAnotherUnitPNNV(option))
    save()
  }

  const setDoseOnChange = (values: string[], medication: IAddMedicationAdministered) => {
    dispatch(setDose(values.length ? values.join('') : null, medication))
    save()
  }

  const setDoseUnitsOnChange = (option: IOption, medication: IAddMedicationAdministered) => {
    dispatch(setDoseUnit(option, medication))
    dispatch(setDoseUnitPNNV(null, medication))
    save()
  }

  const setDoseUnitsPNNVOnChange = (option: IPNNVOption | null, medication: IAddMedicationAdministered) => {
    dispatch(setDoseUnitPNNV(option, medication))
    dispatch(setDoseUnit(null, medication))
    save()
  }

  const setRateUnitOnChange = (option: IOption, medication: IAddMedicationAdministered) => {
    dispatch(setRateUnit(option, medication))
    save()
  }

  const setRouteOnChange = (option: IOption, medication: IAddMedicationAdministered) => {
    dispatch(setRoute(option, medication))
    dispatch(setRoutePNNV(null, medication))
    save()
  }

  const setRoutePNNVOnChange = (option: IPNNVOption | null, medication: IAddMedicationAdministered) => {
    dispatch(setRoutePNNV(option, medication))
    dispatch(setRoute(null, medication))
    save()
  }

  const setRateOnChange = (values: string[], medication: IAddMedicationAdministered) => {
    dispatch(setRate(values.length ? values.join('') : null, medication))
    save()
  }

  const setConcentrationOnChange = (e: CustomEvent, medication: IAddMedicationAdministered) => {
    dispatch(setConcentration(e.detail.value, medication))
    save()
  }

  const onDeleteMedicationAdministered = (AddMedicationAdministered: IAddMedicationAdministered) => {
    dispatch(deleteMedicationAdministered(AddMedicationAdministered))
    save()
    dispatch(setCurrentMedication(null))
  }

  const setWastedOnChange = (medication: IAddMedicationAdministered) => {
    dispatch(setWasted(!medication.wasted, medication))
    save()
  }

  const onAddMedication = () => {
    dispatch(addMedicationAdministered())
    save()
    dispatch(setCurrentMedication(null))
  }

  useEffect(() => {
    const content = document.getElementById(currentMedication?.key || '')
    setTimeout(() => {
      content?.scrollIntoView({
        behavior: 'smooth',
      })
    }, 300)
  }, [currentMedication?.medication])

  const onLookupClick = (e: CustomEvent, medication: IAddMedicationAdministered) => {
    e.preventDefault()
    dispatch(setCurrentMedication(null))
    history.push(TabConstants.ADD_MEDICATIONS_ADMINISTERED_SEARCH)
    dispatch(setMedicationAdministered(medication))
  }

  const setWitness1OnChange = (e: CustomEvent, medication: IAddMedicationAdministered) => {
    e.preventDefault()
    dispatch(setWitness1(e.detail.value, medication))
    save()
  }

  const setWitness2OnChange = (e: CustomEvent, medication: IAddMedicationAdministered) => {
    e.preventDefault()
    dispatch(setWitness2(e.detail.value, medication))
    save()
  }

  const setLotOnChange = (e: CustomEvent, medication: IAddMedicationAdministered) => {
    e.preventDefault()
    dispatch(setLotNumber(e.detail.value, medication))
    save()
  }

  const setResponseOnChange = (option: IOption, medication: IAddMedicationAdministered) => {
    dispatch(setResponseValue(option, medication))
    save()
  }

  const setResponsePNNVOnChange = (option: IPNNVOption | null, medication: IAddMedicationAdministered) => {
    dispatch(setResponseValuePNNV(option, medication))
    save()
  }

  const setAuthorizationOnChange = (option: IOption, medication: IAddMedicationAdministered) => {
    dispatch(setAuthorization(option, medication))
    save()
  }

  const setCommentsOnChange = (e: CustomEvent) => {
    if (e.detail.value === comments) return
    e.preventDefault()
    dispatch(setComments(e.detail.value))
    save()
  }

  const onPTAClick = () => {
    const isPTA = !pta
    dispatch(setPTA(isPTA))
    dispatch(syncPtaWithParentVitals(isPTA))
  }

  const save = () => {
    dispatch(saveMedicationAdministered())
  }

  const tranformToPNNVOption = (option: IOption | null) => {
    const routePNNVOption = {
      id: option?.id,
      name: option?.name,
      pn: option?.isPN && option?.nemsisCode != null ? option?.nemsisCode.toString() : '',
      nv: option?.isNV && option?.nemsisCode != null ? option?.nemsisCode.toString() : '',
    }
    return routePNNVOption
  }

  const checkPerformedBy = useMemo(() => {
    if (assessedBy) {
      const performedByIndex = performedByOptions.findIndex((option) => option.id === assessedBy.id)
      if (performedByIndex === -1) {
        return true
      }
      return false
    }
  }, [assessedBy, performedByOptions])

  useEffect(() => {
    addMedicationAdministeredData.map((medication: IAddMedicationAdministered) => {
      medication.medication?.name &&
        medication.medication.id &&
        dispatch(
          setMedication(
            {
              name: medication.medication?.name || '',
              id: medication.medication.id,
              nemsisCode: medication.medication?.nemsisCode || null,
              sourceDataset: medication.medication?.sourceDataset || null,
            },
            medication,
            medication.medication?.name || ''
          )
        )
      medication.dose && dispatch(setDose(medication.dose, medication))
      medication.rate && dispatch(setRate(medication.rate, medication))
      medication.concentration && dispatch(setConcentration(medication.concentration, medication))
      medication.lotNumber && dispatch(setLotNumber(medication.lotNumber, medication))
    })
    time && dispatch(setTime(time))
    date && dispatch(setDate(date))
    comments && dispatch(setComments(comments))
  }, [addMedicationAdministeredData])

  const setFieldDefaults = () => {
    const fieldDefaults = medicationDefaults?.defaults.filter(
      (field) => field.actionId === currentMedication?.medication?.id.toString()
    )

    fieldDefaults?.forEach((fieldDefault) => {
      const fieldName = fieldDefault.fieldName
      const defaultValue = fieldDefault.defaultValue

      // Dose
      if (fieldName === 'field1') {
        dispatch(setDose(defaultValue || '', currentMedication))
      }

      // Route
      else if (fieldName === 'field2') {
        const routeOption = routeOptions.find((option) => option.id === Number(defaultValue))
        if (routeOption?.isPN || routeOption?.isNV) {
          dispatch(setRoutePNNV(tranformToPNNVOption(routeOption) || null, currentMedication))
        } else {
          dispatch(setRoute(routeOption || null, currentMedication))
        }
      }
      // Dose Unit
      else if (fieldName === 'field6') {
        const doseUnitOption = doseUnitOptions.find((option) => option.id === Number(defaultValue))
        if (doseUnitOption?.isPN || doseUnitOption?.isNV) {
          dispatch(setDoseUnitPNNV(tranformToPNNVOption(doseUnitOption) || null, currentMedication))
        } else {
          dispatch(setDoseUnit(doseUnitOption || null, currentMedication))
        }
      }

      // Rate
      else if (fieldName === 'field10') {
        dispatch(setRate(defaultValue || '', currentMedication))
      }

      // Rate Unit
      else if (fieldName === 'field11') {
        const rateUnitOption = rateUnitOptions.find((option) => option.id === Number(defaultValue))
        dispatch(setRateUnit(rateUnitOption || null, currentMedication))
      }
    })

    save()
  }

  useIonViewWillEnter(() => {
    const ageRange = getAgeRangeGroupId(dOB, age, ageUnit)
    const defaultsForAgeRange = getDefaultsForAgeRange(defaults, 'Medication', ageRange)

    if (defaultsForAgeRange) dispatch(setMedicationDefaults(defaultsForAgeRange))
  }, [defaults, dOB, age, ageUnit])

  useEffect(() => {
    if (!isFirstVisit || !medicationDefaults || !currentMedication) return

    setFieldDefaults()
  }, [currentMedication])

  return (
    <>
      {procedureLoader ? (
        <IonSpinner className='procedure-loader' data-testid='Spinner' />
      ) : (
        <IonList>
          <IonItemDivider color='light' className='ion-padding'>
            <IonLabel className='grid-item-divider-label' color='medium'>
              {t('addMedicationAdministered.addMedicationAdministered')}
            </IonLabel>
          </IonItemDivider>

          <IonGrid className='ion-no-padding'>
            <IonRow>
              <IonCol>
                <TimesRow
                  elementId='Date-Time'
                  header={t('addMedicationAdministered.dateTime')}
                  time={time}
                  date={date}
                  timeAction={AddMedicationAdministeredConstants.SET_TIME}
                  dateAction={AddMedicationAdministeredConstants.SET_DATE}
                  currentDateTimeAction={AddMedicationAdministeredConstants.SET_DATE_TIME}
                  disabled={!!isEkgImport}
                  subHeader={isEkgImport ? t('times.fromVitals') : ''}
                  cardinalityKey={key}
                />
              </IonCol>
            </IonRow>

            <hr className='field-divider' />

            <IonRow>
              <IonCol className='picklist'>
                <SingleSelect
                  isPHI={isPHI}
                  dataTestID='PerformedBy'
                  options={performedByOptions}
                  selectedOption={assessedBy}
                  listHeader={t('addMedicationAdministered.assessedBy')}
                  label={t('addMedicationAdministered.assessedBy')}
                  action={setAssessedByOnChange}
                />
                {checkPerformedBy && (
                  <IonLabel color='danger'>{t('addMedicationAdministered.PerformedByError')}</IonLabel>
                )}
              </IonCol>
            </IonRow>

            <hr className='field-divider' />

            <IonRow>
              <IonCol className='picklist'>
                <SingleSelect
                  isPHI={isPHI}
                  dataTestID='RolePerforming'
                  options={rolePerformingOptions}
                  selectedOption={rolePerforming}
                  listHeader={t('addMedicationAdministered.rolePerforming')}
                  label={t('addMedicationAdministered.rolePerforming')}
                  action={setRolePerformingOnChange}
                  selectedPNNVOption={rolePerformingPNNV}
                  pnnvAction={setRolePerformingPNNVOnChange}
                />
              </IonCol>
            </IonRow>
            <hr className='field-divider' />
            <IonRow>
              <IonCol className='picklist'>
                <SingleSelect
                  dataTestID='AdministeredByAnotherUnit'
                  options={administeredByAnotherUnitOptions}
                  selectedOption={administeredByAnotherUnit}
                  listHeader={t('addMedicationAdministered.administeredByAnotherUnit')}
                  label={t('addMedicationAdministered.administeredByAnotherUnit')}
                  action={setAdministeredByAnotherUnitOnChange}
                  selectedPNNVOption={administeredByAnotherUnitPNNV}
                  pnnvAction={setAdministeredByAnotherUnitPNNVOnChange}
                />
              </IonCol>
            </IonRow>
          </IonGrid>
          <IonItemDivider color='light' />

          <IonRow>
            <IonCol className='picklist'>
              <IonItem className='toggle-item' lines='none'>
                <IonLabel>{t('procedure.header.pta')}</IonLabel>
                <IonToggle
                  data-testid='PTA'
                  slot='start'
                  name='pta'
                  checked={pta}
                  disabled={!!isEkgImport}
                  onClick={onPTAClick}
                  color='zoll-blue-secondary'
                />
              </IonItem>
            </IonCol>
          </IonRow>

          <IonItemDivider color='light' />
          <IonList id='AddMedicationAdministeredData' className='list-background'>
            {addMedicationAdministeredData.map((medication: IAddMedicationAdministered, index: number) => (
              <IonItemSliding key={medication.key}>
                <IonCard
                  id={medication?.key}
                  className='ion-no-margin ion-margin-vertical'
                  data-testid='AddMedicationAdministered'>
                  <IonCardContent>
                    <IonGrid className='no-padding'>
                      <IonRow>
                        <IonCol>
                          {t('addMedicationAdministered.medication')} {index + 1}
                        </IonCol>
                        <IonButtons>
                          <IonButton
                            data-testid={`deleteMedicationAdministered${index}`}
                            onClick={() => onDeleteMedicationAdministered(medication)}>
                            <IonIcon icon={trashOutline} />
                          </IonButton>
                        </IonButtons>
                      </IonRow>
                      <IonRow>
                        <IonCol>
                          <IonItem className='ion-align-items-end' lines='none'>
                            <IonButton
                              fill='clear'
                              data-testid={`Lookup${index}`}
                              onClick={(e: any) => {
                                onLookupClick(e, medication)
                              }}
                              color='primary-alt'
                              className='ion-no-margin'>
                              {medication.medication && medication.medication.name ? (
                                <IonLabel className='medicationNameLabel'>{medication.medication?.name}</IonLabel>
                              ) : (
                                t('addMedicationAdministered.lookup')
                              )}
                              <IonIcon slot='start' icon={search} />
                            </IonButton>
                          </IonItem>
                        </IonCol>
                      </IonRow>
                      {hasMedicationValue(medication) && (
                        <>
                          <hr className='field-divider' />
                          <IonRow>
                            <IonCol className='picklist'>
                              <NumberPad
                                elementId={`Dose${index}`}
                                dataTestId={`Dose${index}`}
                                values={medication.dose ? medication.dose.split('') : []}
                                label={t('addMedicationAdministered.dose')}
                                validCharacters={numberPadKeysWithDecimal}
                                onNumPadClick={(values) => {
                                  setDoseOnChange(values, medication)
                                }}
                              />
                              <Validation
                                id={AddMedicationAdministeredConstants.SET_DOSE}
                                cardinalityKey={medication.key}
                              />
                            </IonCol>

                            <IonCol className='picklist'>
                              <SingleSelect
                                dataTestID={`DoseUnits${index}`}
                                options={doseUnitOptions}
                                selectedOption={medication.doseUnit}
                                listHeader={t('addMedicationAdministered.doseUnit')}
                                label={t('addMedicationAdministered.doseUnit')}
                                action={setDoseUnitsOnChange}
                                dispatchData={medication}
                                selectedPNNVOption={medication.doseUnitPNNV}
                                pnnvAction={setDoseUnitsPNNVOnChange}
                              />
                            </IonCol>
                          </IonRow>
                          <hr className='field-divider' />
                          {!medication.wasted && (
                            <>
                              <IonRow>
                                <IonCol className='picklist'>
                                  <NumberPad
                                    elementId={`Rate${index}`}
                                    dataTestId={`Rate${index}`}
                                    values={medication.rate ? medication.rate.split('') : []}
                                    label={t('addMedicationAdministered.rate')}
                                    validCharacters={numberPadKeysWithDecimal}
                                    onNumPadClick={(values) => {
                                      setRateOnChange(values, medication)
                                    }}
                                  />
                                  <Validation
                                    id={AddMedicationAdministeredConstants.SET_RATE}
                                    cardinalityKey={medication.key}
                                  />
                                </IonCol>

                                <IonCol className='picklist'>
                                  <SingleSelect
                                    dataTestID={`RateUnit${index}`}
                                    options={rateUnitOptions}
                                    selectedOption={medication.rateUnit}
                                    listHeader={t('addMedicationAdministered.rateUnit')}
                                    label={t('addMedicationAdministered.rateUnit')}
                                    action={setRateUnitOnChange}
                                    dispatchData={medication}
                                  />
                                </IonCol>
                              </IonRow>

                              <hr className='field-divider' />

                              <IonRow>
                                <IonCol className='picklist'>
                                  <SingleSelect
                                    label={t('addMedicationAdministered.route')}
                                    listHeader='Route'
                                    dataTestID={`Route${index}`}
                                    options={routeOptions}
                                    action={setRouteOnChange}
                                    selectedOption={medication.route}
                                    dispatchData={medication}
                                    selectedPNNVOption={medication.routePNNV}
                                    pnnvAction={setRoutePNNVOnChange}
                                  />
                                </IonCol>
                              </IonRow>

                              <hr className='field-divider' />

                              <IonRow>
                                <IonCol className='picklist'>
                                  <SInput
                                    label={t('addMedicationAdministered.concentration')}
                                    dataTestID={`Concentration-${index}`}
                                    inputValue={medication.concentration}
                                    onChangeAction={(e) => {
                                      setConcentrationOnChange(e, medication)
                                    }}
                                  />
                                  <Validation
                                    id={AddMedicationAdministeredConstants.SET_CONCENTRATION}
                                    cardinalityKey={medication.key}
                                  />
                                </IonCol>
                              </IonRow>

                              <hr className='field-divider' />
                            </>
                          )}
                          <IonRow>
                            <IonCol className='picklist'>
                              <IonItem className='toggle-item' lines='none'>
                                <IonToggle
                                  data-testid={`Wasted${index}`}
                                  slot='start'
                                  checked={medication.wasted}
                                  onClick={() => setWastedOnChange(medication)}
                                />
                                <IonLabel>{t('addMedicationAdministered.wasted')}</IonLabel>
                              </IonItem>
                            </IonCol>
                          </IonRow>
                          <hr className='field-divider' />
                          {medication.wasted && (
                            <>
                              <IonRow>
                                <IonCol className='picklist'>
                                  <SInput
                                    label={t('addMedicationAdministered.witness1')}
                                    dataTestID={`Witness1-${index}`}
                                    inputValue={medication.witness1}
                                    onChangeAction={(e) => {
                                      setWitness1OnChange(e, medication)
                                    }}
                                  />
                                </IonCol>
                              </IonRow>

                              <hr className='field-divider' />

                              <IonRow>
                                <IonCol className='picklist'>
                                  <SInput
                                    label={t('addMedicationAdministered.witness2')}
                                    dataTestID={`Witness2-${index}`}
                                    inputValue={medication.witness2}
                                    onChangeAction={(e) => {
                                      setWitness2OnChange(e, medication)
                                    }}
                                  />
                                </IonCol>
                              </IonRow>

                              <hr className='field-divider' />
                            </>
                          )}

                          <IonRow>
                            <IonCol className='picklist'>
                              <SInput
                                label={t('addMedicationAdministered.lotNumber')}
                                dataTestID={`lotNumber${index}`}
                                inputValue={medication.lotNumber}
                                onChangeAction={(e) => {
                                  setLotOnChange(e, medication)
                                }}
                              />
                              <Validation
                                id={AddMedicationAdministeredConstants.SET_LOT_NUMBER}
                                cardinalityKey={medication.key}
                              />
                            </IonCol>
                          </IonRow>
                          <hr className='field-divider' />
                          {!medication.wasted && (
                            <>
                              <IonRow>
                                <IonCol className='picklist'>
                                  <MultiSelect
                                    dataTestID='ComplicationsOptions'
                                    listHeader={t('addMedicationAdministered.complications')}
                                    options={complicationsOptions}
                                    savedValues={medication.complications}
                                    action={setComplications}
                                    dispatchData={medication}
                                    callback={saveMedicationAdministered}
                                    label={t('addMedicationAdministered.complications')}
                                  />
                                </IonCol>
                              </IonRow>

                              <hr className='field-divider' />

                              <IonRow>
                                <IonCol className='picklist'>
                                  <SingleSelect
                                    label={t('addMedicationAdministered.response')}
                                    listHeader='Response'
                                    dataTestID={`Response${index}`}
                                    options={responseOptions}
                                    action={setResponseOnChange}
                                    selectedOption={medication.response}
                                    dispatchData={medication}
                                    selectedPNNVOption={medication.responsePNNV}
                                    pnnvAction={setResponsePNNVOnChange}
                                  />
                                </IonCol>
                              </IonRow>

                              <hr className='field-divider' />
                            </>
                          )}
                        </>
                      )}
                    </IonGrid>
                  </IonCardContent>
                </IonCard>
                <IonItemOptions side='end' className='delete-swipe'>
                  <IonItemOption
                    data-testid={`DeleteIcon${index}`}
                    color='danger'
                    onDrag={() => onDeleteMedicationAdministered(medication)}
                    onClick={() => onDeleteMedicationAdministered(medication)}>
                    <IonIcon slot='icon-only' icon={trash} />
                  </IonItemOption>
                </IonItemOptions>
              </IonItemSliding>
            ))}
            <IonRow className='ion-float-right'>
              <IonButton
                data-testid='AddMedication'
                className='transform-none'
                fill='clear'
                color='primary-alt'
                onClick={() => onAddMedication()}>
                <IonIcon icon={addCircleOutline} />
                <IonLabel>{t('addMedicationAdministered.addMedication')}</IonLabel>
              </IonButton>
            </IonRow>
          </IonList>

          <IonItemDivider color='light' />

          <IonList>
            <IonRow>
              <IonCol className='picklist'>
                <SingleSelect
                  label={t('addMedicationAdministered.authorization')}
                  listHeader='Authorization'
                  dataTestID='Authorization'
                  options={authorizationOptions}
                  action={setAuthorizationOnChange}
                  selectedOption={authorization}
                />
              </IonCol>
            </IonRow>

            <hr className='field-divider' />

            <IonRow>
              <IonCol className='picklist'>
                <IonItem lines='none'>
                  <IonLabel color='medium' className={textAreaStyles['text-area-label']} position='stacked'>
                    {t('addMedicationAdministered.comments')}
                  </IonLabel>
                  <div data-private={isPHI} style={getStyleForPHI(isPHI)}>
                    <IonTextarea
                      data-testid='Comments'
                      value={comments}
                      onIonChange={(e) => {
                        setCommentsOnChange(e)
                      }}
                      rows={5}
                      maxlength={1000}
                      autocapitalize='sentences'
                      onFocus={onFocus}
                      onBlur={onBlur}
                      color='primary-alt'
                    />
                  </div>
                  <div className='footer'>
                    <span>
                      <Validation id={AddMedicationAdministeredConstants.SET_COMMENTS} cardinalityKey={key} />
                    </span>
                    <h1 className={comments.length < 1000 ? 'text-color-blue-tint' : 'text-color-red'}>
                      {comments.length}/1000
                    </h1>
                  </div>
                </IonItem>
              </IonCol>
            </IonRow>
          </IonList>
        </IonList>
      )}
      {isFocused && (
        <IonItemDivider color='light' className={isTabletDevice ? 'bottom-divider-tablet' : 'bottom-divider-mobile'} />
      )}
    </>
  )
}

export default AddMedicationAdministeredContent
